import React from 'react';
import { useNavigate } from 'react-router-dom';

const CanAIPredictPublicOpinion = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigate('/blog')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">
            Can Human AI Predict Public Opinion? A New Study Says Yes—If You Train It Right
          </h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>January 2025</span>
            <span className="mx-2">•</span>
            <span>10 min read</span>
            <span className="mx-2">•</span>
            <span>Research Deep Dive</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <p className="text-lg">
            How well can AI reflect the diverse opinions of real people? That's the central question driving a groundbreaking new study from a team at UC Berkeley and Microsoft Research. While large language models (LLMs) like GPT and Llama are widely used to generate text and simulate human responses, they often struggle to accurately predict how actual subpopulations think—especially across race, income, region, education, and ideology.
          </p>

          <p>
            This paper, <strong>"Language Model Fine-Tuning on Scaled Survey Data,"</strong> takes a bold step forward by showing that fine-tuning LLMs on real survey data—rather than relying on prompt engineering alone—dramatically improves their ability to predict the actual distribution of public opinion.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">The Problem</h2>
          <p>
            Most past approaches tried to steer LLMs with demographic prompts: "Pretend you're a low-income Asian woman from the South—how would you answer this survey question?" But that only gets you so far. These models frequently echoed the views of overrepresented groups (e.g., the wealthy and highly educated), missed the nuance of real-world variation, and often generated biased or stereotypical outputs for underrepresented populations.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">The Method: Fine-Tuning on Survey Data</h2>
          <p>
            Instead of prompting, the authors fine-tuned several open-source LLMs (Llama-2-7B, Mistral-7B, Llama-3-70B, etc.) on a new, massive dataset they built called <strong>SubPOP</strong>, which contains:
          </p>
          <ul>
            <li><strong>3,362 questions</strong></li>
            <li><strong>70,000 subpopulation-response distribution pairs</strong></li>
            <li>Drawn from well-established public opinion surveys like Pew's American Trends Panel and the General Social Survey</li>
          </ul>

          <p>
            Each data point links a question, a demographic subpopulation (e.g., "college-educated Hispanic women in the West"), and a distribution of real human responses.
          </p>

          <p>
            The key innovation? They train the LLMs to match these human response distributions, not just pick a single answer. That means the model doesn't just "guess" the most likely response—it learns to reflect the range of beliefs across a group.
          </p>

          <p>
            They used LoRA fine-tuning and a forward KL divergence loss to push model predictions closer to the real-world response patterns.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Results: Up to 46% Better Match with Humans</h2>
          <p>
            Across two benchmarks—OpinionQA and a new evaluation set (SubPOP-Eval)—the fine-tuned models:
          </p>
          <ul>
            <li><strong>Reduced the LLM-human gap by 32–46%</strong> compared to zero-shot and few-shot prompting</li>
            <li><strong>Outperformed Modular Pluralism,</strong> a leading method that trains separate LLMs for different communities</li>
            <li><strong>Generalized well to new survey topics,</strong> unseen questions, and new subpopulations—even age groups and education levels not seen during training</li>
          </ul>

          <p>
            For example, fine-tuning reduced the average Wasserstein Distance (WD)—a measure of how far the AI's opinion distribution is from real human distributions—from ~0.18 to ~0.10.
          </p>

          <p>
            Even better: the performance scaled predictably as more training data was added. More high-quality survey data = better model accuracy.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Key Findings and Takeaways</h2>
          <ul>
            <li><strong>Fine-tuning on real survey data works</strong>—far better than prompts alone</li>
            <li><strong>Models adapt to subpopulations,</strong> even those not seen during training, and show true "steerability" based on demographic context</li>
            <li><strong>Opinion distributions matter.</strong> Just predicting the top answer misses the diversity and nuance within a group. Modeling full distributions is essential</li>
            <li><strong>More data still improves results.</strong> The model hasn't hit a ceiling—performance continues to rise with more high-quality training samples</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Why This Matters</h2>
          <p>
            This isn't just a technical breakthrough—it's a potential revolution in how we conduct public opinion research.
          </p>
          <p>
            Imagine:
          </p>
          <ul>
            <li>Designing better surveys with AI-simulated pilot runs</li>
            <li>Identifying biases or blind spots before going to field</li>
            <li>Understanding what subgroups believe without waiting weeks for responses</li>
          </ul>
          <p>
            Rather than replacing human respondents, this approach augments researchers' ability to pretest, refine, and scale opinion research faster and more inclusively.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Link to Code & Dataset</h2>
          <p>
            The team has open-sourced their data and training pipeline:
            <br />
            <a href="https://github.com/JosephJeesungSuh/subpop" className="text-pink-400 underline hover:text-pink-300" target="_blank" rel="noopener noreferrer">
              https://github.com/JosephJeesungSuh/subpop
            </a>
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Final Thoughts</h2>
          <p>
            This paper is a landmark in AI-for-social-science research. It shows that with the right data, you can align AI systems to better reflect diverse human realities. But it also underscores a deeper truth: if you want AI to reflect society, you need to train it on society's data—not just the internet.
          </p>
          <p>
            As LLMs become more embedded in decision-making systems, getting this right isn't optional—it's critical.
          </p>

          <p className="text-lg font-semibold text-white">
            At HumanAI, we're taking these insights and building the next generation of AI respondents that truly understand and reflect human diversity.
          </p>

          <p className="mt-6 font-semibold">
            <a href="#request-access" className="text-pink-400 underline hover:text-pink-300">Request early access</a> to see how we're making this future a reality.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>

        </div>
      </div>
    </div>
  );
};

export default CanAIPredictPublicOpinion; 